
export class ReportTemplate {
	id: string;
	content: IReportTemplateContent;
	name: string;
	isPrivate: boolean;
	commentAtBeginning: string;
	commentAtEnd: string;

	static readonly generatorType_organizational: string = 'Organizational';
	static readonly generatorType_servicestructure: string = 'ServiceStructure';

	constructor(init?: Partial<ReportTemplate>) {
		Object.assign(this, init);
	}

	static fromAM(templateAM: IReportTemplateAM): ReportTemplate {
		return new ReportTemplate({
			id: templateAM.id,
			name: templateAM.name,
			isPrivate: templateAM.isPrivate,
			content: templateAM.content,
			commentAtBeginning: templateAM.commentAtBeginning,
			commentAtEnd: templateAM.commentAtEnd,
		});
	}

	toAM(): any {
		return {
			id: this.id,
			name: this.name,
			isPrivate: this.isPrivate,
			content: JSON.stringify(this.content),
			commentAtBeginning: this.commentAtBeginning,
			commentAtEnd: this.commentAtEnd,
		};
	}
}

export class ComparisonType {
	name: string;
	columns: ComparisonTargetColumn[];

	constructor(init?: Partial<ComparisonType>) {
		Object.assign(this, init);
	}

	static fromAM(am: IComparisonType): ComparisonType {
		return new ComparisonType({
			name: am.name,
			columns: am.columns.map(columnAM => ComparisonTargetColumn.fromAM(columnAM))
		});
	}
}

export class ComparisonTargetColumn {
	name: string;
	required: boolean;

	constructor(init?: Partial<ComparisonTargetColumn>) {
		Object.assign(this, init);
	}

	static fromAM(am: IComparisonTargetColumn) {
		return new ComparisonTargetColumn({
			name: am.name,
			required: am.required
		});
	}
}

export interface IReportTemplateAM {
	id: string;
	isPrivate: boolean;
	name: string;
	serviceStructureId: string;
	content: IReportTemplateContent;
	commentAtBeginning: string;
	commentAtEnd: string;
}

export interface IReportTemplateContent {
	source?: IReportSource;
	columns?: IReportTemplateColumn[];
	rows: IReportTemplateRow[];
}

export interface IReportSource {
	filters: IReportFilterCollection;
}

export interface IReportFilterCollection {
	includeUntargetedFinancialEntries?: boolean;
	includeFinancialEntriesInAccountClassOther?: boolean;
	accountClasses?: string[];
	accountGroups?: string[];
	accounts?: string[];
	accountTags?: string[];
	forbiddenAccountTags?: string[];
	dimensionValues?: string[];
	excludeCostCalculations?: boolean;
	excludeInternalEntries?: boolean;
	excludePassedOnEntries?: boolean;
	financialPrecision?: ReportFinancialPrecision;
	measures?: string[];
	forbiddenMeasures?: string[];
}

export interface IReportTemplateColumn {
	id: string;
	type: ReportTemplateColumnType;
	scope?: string;
	municipality?: number | string | undefined;
	title?: string;
}

export enum ReportTemplateColumnType {
	Data = 'Data',
	Comparison = 'Comparison'
}

export interface IDataColumn extends IReportTemplateColumn {
	municipality: number | string;
	title: string;
	scope: string;
	organizationalUnit: string;
	costCenter: string;
}

export interface IComparisonColumn extends IReportTemplateColumn {
	columnIds: string[];
	comparisonType: IComparisonType;
}

export interface IComparisonType {
	name: string;
	columns: IComparisonTargetColumn[];
}

export interface IComparisonTargetColumn {
	name: string;
	required: boolean;
}

export interface IReportTemplateRow {
	type: ReportTemplateRowType;
	service: string;
	organizationalUnit: string;
	costCenter: string;
}

export enum ReportTemplateRowType {
	AccountClass = 'AccountClass',
	AccountGroup = 'AccountGroup',
	GeneratedRows = 'GeneratedRows',
	Group = 'Group',
	Measure = 'Measure',
	Population = 'Population',
	AccountTag = 'AccounTag',
	PerformanceIndicator = 'PerformanceIndicator',
	FinancialSum = 'FinancialSum',
	FinancialEntryType = 'FinancialEntryType',
}

export interface IAccountClassRow extends IReportTemplateRow {
	type: ReportTemplateRowType.AccountClass;
	code: string;
}

export interface IAccountGroupRow extends IReportTemplateRow {
	type: ReportTemplateRowType.AccountGroup;
	code: string;
}

export enum FinancialEntryType {
	External = 'External',
	PassedOnRevenue = 'PassedOnRevenue',
	PassedOnExpenses = 'PassedOnExpenses',
	InternalRevenue = 'InternalRevenue',
	InternalExpenses = 'InternalExpenses',
}
export interface IFinancialSumRow extends IReportTemplateRow {
	type: ReportTemplateRowType.FinancialSum;
	externalMunicipalitiesDivisionInclusion: string;
	performanceIndicator?: string;
}

export interface IFinancialEntryTypeRow extends IGroupRow {
	type: ReportTemplateRowType.FinancialEntryType;
	types?: FinancialEntryType[];
}

export interface IMeasureRow extends IReportTemplateRow {
	type: ReportTemplateRowType.Measure;
	code: string;
}

export interface IPerformanceIndicatorRow extends IReportTemplateRow {
	type: ReportTemplateRowType.PerformanceIndicator;
	code: string;
}

export interface IPopulationRow extends IReportTemplateRow {
	type: ReportTemplateRowType.Population;
	fromAge: number;
	toAge: number;
}

export interface IGroupRow extends IReportTemplateRow {
	rows: IReportTemplateRow[];
}

export interface IGeneratedRow extends IReportTemplateRow {
	type: ReportTemplateRowType.GeneratedRows;
	generator: string;
	modifications: IRowGeneratorModification[];
	parameters: IRowGeneratorParameters;
}

export interface IRowGeneratorModification {
	operation: RowGeneratorModificationOperation;
	anchor: string;
}

export enum RowGeneratorModificationOperation {
	Delete = 'Delete'
}

export enum CommonRowsSet {
	Basic = 'Basic',
	IncomeStatement = 'IncomeStatement' // "tuloslaskelmaraportti"
}

export interface ICommonRow {
	rowTemplate: IReportTemplateRow;
	accountGroupSeparation: boolean;
	accountSeparation: boolean;
	/** "Tarkenteet"; missing in templates saved before the feature, read as false */
	dimensionSeparation?: boolean;
	typeSeparation: boolean;
}

export interface IRowGeneratorParameters {
	serviceStructureId: string;
	organizationId: string;
	identifiers: string[];
	includeMeasures: boolean;
	includePerformanceIndicators: boolean;
	includeCostCenters: boolean;
	includeCostCenterDimensionFilters: boolean;
	includeSumSection: boolean;
	includeServices: boolean;
	scopeTypeId: string;
	commonRowsSet: CommonRowsSet;
	selectedCommonRows: ICommonRow[];

	/*** Obsolete, use selectedCommonRows */
	commonRows: IReportTemplateRow[];
	/*** Obsolete, use selectedCommonRows */
	accountGroupSeparationForRevenues: boolean;
	/*** Obsolete, use selectedCommonRows */
	accountGroupSeparationForOperatingExpenses: boolean;
}

export enum ReportFinancialPrecision {
	Decimal = 0,
	Integer = 1,
	Thousands = 2,
	None = 3
}
